import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router";
import { cx } from "../../ui/cx";
import { useMe } from "../session";
import { readFlag, writeFlag } from "../shell/persist";
import { Sidebar } from "../shell/Sidebar";
import { TopBar } from "../shell/TopBar";
import styles from "./AppShell.module.css";

/** Top bar + app sidebar + 20px-gutter main area. Renders `children` instead of the route outlet when given (404). */
export function AppShell({ children }: { children?: ReactNode }) {
  const me = useMe();
  // Per user, like Clockify, so two people sharing a browser keep their own preference.
  const collapseKey = `sidebarCollapsed:${me.user.id}`;
  const [collapsed, setCollapsed] = useState(() => readFlag(collapseKey));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setCollapsed(readFlag(collapseKey));
  }, [collapseKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: closing the drawer is the point of watching the path
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const toggle = useCallback(() => {
    setCollapsed((v) => {
      writeFlag(collapseKey, !v);
      return !v;
    });
  }, [collapseKey]);

  return (
    <div
      className={cx(styles.shell, collapsed && styles.collapsed)}
      data-sidebar={collapsed ? "collapsed" : "expanded"}
    >
      <TopBar onOpenNavigation={() => setMobileOpen(true)} />
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={toggle}
        mobileOpen={mobileOpen}
        onNavigate={() => setMobileOpen(false)}
      />
      {mobileOpen && (
        <button
          type="button"
          className={styles.backdrop}
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <main className={styles.main}>{children ?? <Outlet />}</main>
    </div>
  );
}
